'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useStore } from '@/store/store';
import { getCookieValue } from '@/utils/cookies/client';
import { CANARIES, canaryCookieName, toVariant } from '@/logic/experiments/canaries';
import { trackExperimentExposure } from '@/tracking/amplitude/events/general/experiment-exposure';

const exposed = new Set<string>();

/**
 * Fires one Amplitude `$exposure` per canary the visitor is assigned to, reading the `canary_<id>`
 * cookies the middleware set. Local evaluation emits no exposure of its own, so the browser has to.
 *
 * Control arms are reported too, otherwise the arms are not comparable. Mounted once in the layout
 * so navigation picks up cookies that only appear on the routes they gate.
 *
 * Only canaries that decide the current path are reported: the cookie is scoped to `/`, so without
 * that check a later load on an unrelated route would report an assignment that decided nothing.
 *
 * Logged-in only, matching the middleware. The cookie outlives a logout, so without the `accountID`
 * gate a logged-out visitor would report an arm they were never served.
 */
export const CanaryExposure = () => {
    const sendEvent = useStore((state) => state.tracking.sendEvent);
    const accountID = useStore((state) => state.userContext.accountSettings.accountID);
    const pathname = usePathname();

    useEffect(() => {
        if (!pathname || !accountID) {
            return;
        }

        const events = CANARIES.flatMap((canary) => {
            if (exposed.has(canary.flagKey) || !canary.matches(pathname)) {
                return [];
            }

            const variant = toVariant(canary, getCookieValue(canaryCookieName(canary)));
            if (!variant) {
                return [];
            }

            exposed.add(canary.flagKey);
            return [trackExperimentExposure({ flag_key: canary.flagKey, variant })];
        });

        if (events.length > 0) {
            sendEvent(events);
        }
    }, [sendEvent, pathname, accountID]);

    return null;
};
